import { FC, useState } from 'react';
import { of } from 'rxjs';
import { useTranslation } from 'react-i18next';
import { useLoggedinUser, useService } from '@hooks';
import {
	DealerConfig,
	dealerConfigService,
	FleetManagerClearDataDto,
	fleetManagerService,
	notificationService,
	Permission,
	SystemConfigKey,
	systemConfigService,
	SystemConfigValueDto
} from '@services';
import { RxUtils, Tr } from '@utils';
import { Button, Stack, Typography } from '@mui/material';
import { EmptyEntityPageLayout, PortalPageTitle } from '@components/portal/common';
import { EntityList, EntityListItem } from '@components/common';
import {
	DealerFuelPriceConfiguration,
	DealerPriceConfiguration,
	DealerStockConfiguration,
	FleetManagerClearDataDialog,
	SystemConfigurations
} from '@components/portal/configuration';
import { ReactComponent as Illustration } from '@assets/illustrations/no-stock-added.svg';

const paddingX = 9;

export const Configuration: FC = () => {
	const { t } = useTranslation('portal');
	const user = useLoggedinUser();
	const [cleanDataModalVisible, setCleanDataModalVisible] = useState(false);
	const { data: dealerConfig, trigger: triggerDealerConfigFetch } = useService(
		() => (user?.hasPermission([Permission.STOCK_WRITE]) ? dealerConfigService.find() : of()),
		[user]
	);
	const { data: systemConfigs, trigger: triggerSystemConfigFetch } = useService(
		() => (user?.isAdmin() ? systemConfigService.getAll() : of()),
		[user]
	);
	const hasEditableConfig = user?.hasPermission([Permission.STOCK_WRITE]) || user?.isAdmin() || user?.isFleetManager();

	const onDealerConfigChange = (config: Partial<DealerConfig>) =>
		RxUtils.promisify(
			dealerConfigService.update(config),
			() => (triggerDealerConfigFetch(), notificationService.sendSuccess(t('configuration.updated-successfully')))
		);
	const onSystemConfigChange = (key: SystemConfigKey, value: SystemConfigValueDto) =>
		RxUtils.promisify(
			systemConfigService.update(key, value),
			() => (triggerSystemConfigFetch(), notificationService.sendSuccess(t('configuration.updated-successfully')))
		);
	const onClearData = (dto: Partial<FleetManagerClearDataDto>) =>
		RxUtils.promisify(fleetManagerService.deleteOrganiztionInfo(dto), () =>
			notificationService.sendSuccess(t('configuration.clear-data-successfully'))
		);

	return (
		<Stack direction="column" spacing={3} sx={{ height: 1.0, maxHeight: 1.0, paddingY: 5 }}>
			<PortalPageTitle
				paddingX={paddingX}
				title={<Tr.Portal path="configuration.title" />}
				subtitle={<Tr.Portal path="configuration.subtitle" />}
			/>

			{hasEditableConfig ? (
				<>
					<EntityList entities={[{}]} hideSelectAllButton paddingX={paddingX}>
						{dealerConfig ? (
							<>
								<EntityListItem
									sx={{ paddingRight: paddingX }}
									notSelectable
									accordionTrigger="icon-button"
									expandedInitially
									expandedContent={<DealerStockConfiguration config={dealerConfig} onChange={onDealerConfigChange} />}
								>
									<Typography sx={{ fontWeight: 600 }}>
										<Tr.Portal path="configuration.dealer-config.stock.title" />
									</Typography>
								</EntityListItem>
								<EntityListItem
									sx={{ paddingRight: paddingX }}
									notSelectable
									accordionTrigger="icon-button"
									expandedInitially
									expandedContent={<DealerPriceConfiguration config={dealerConfig} onChange={onDealerConfigChange} />}
								>
									<Typography sx={{ fontWeight: 600 }}>
										<Tr.Portal path="configuration.dealer-config.price.title" />
									</Typography>
								</EntityListItem>
								<EntityListItem
									sx={{ paddingRight: paddingX }}
									notSelectable
									accordionTrigger="icon-button"
									expandedInitially
									expandedContent={<DealerFuelPriceConfiguration config={dealerConfig} onChange={onDealerConfigChange} />}
								>
									<Typography sx={{ fontWeight: 600 }}>
										<Tr.Portal path="configuration.dealer-config.fuel-price.title" />
									</Typography>
								</EntityListItem>
							</>
						) : null}
						{systemConfigs ? (
							<EntityListItem
								sx={{ paddingRight: paddingX }}
								notSelectable
								accordionTrigger="icon-button"
								expandedInitially
								expandedContent={<SystemConfigurations config={systemConfigs} onChange={onSystemConfigChange} />}
							>
								<Typography sx={{ fontWeight: 600 }}>
									<Tr.Portal path="configuration.system-config.title" />
								</Typography>
							</EntityListItem>
						) : null}
					</EntityList>
					<Stack direction="row" alignItems="center" justifyContent="center">
						<Button variant="contained" onClick={() => setCleanDataModalVisible(true)}>
							<Tr.Portal path="configuration.fleet-manager.clear-data.clear-data-button" />
						</Button>
						<FleetManagerClearDataDialog
							open={cleanDataModalVisible}
							onClose={() => setCleanDataModalVisible(false)}
							onSubmit={(dto) => (onClearData(dto), setCleanDataModalVisible(false))}
						/>
					</Stack>
				</>
			) : (
				<EmptyEntityPageLayout illustration={<Illustration />} title={<Tr.Portal path="configuration.no-config-title" />} />
			)}
		</Stack>
	);
};
